import type { BulkAssignHomeDevicesInput, DeviceDto, GameAccountDto } from '@gc/shared';

/**
 * The game-accounts page's bulk tools and "Made for PC" (owner, 2026-09-23),
 * as plain functions — no DOM emulation ships in this workspace, so the
 * parser, the proposer and the filters ARE the seam, as `gameAccountForm.ts`
 * is for the drawer.
 *
 * The owner's words: the PC password manager should have "This PC's
 * credentials" — the accounts actually made for that PC. The venue's sixty
 * Steam accounts are named "Gamers escape 10 (BO6)" / "(BO7)", one pair per
 * PC, and nothing in the data said which PC. "Assign to PCs by number" reads
 * the number in each name and proposes the PC whose name carries the same
 * number; the owner edits and confirms the preview, and only then is anything
 * written.
 *
 * THE ONE RULE THE PROPOSER NEVER BENDS: it does not guess. Account 3 with
 * both PC-3 and VPC-3 on the floor is a question for the owner, not a coin
 * toss — the row is marked for a manual choice and proposes nothing.
 */

/* -------------------------------------------------------------------------- */
/* Numbers in names                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The number an account is named with: "Gamers escape 10 (BO6)" → 10.
 *
 * Bracketed text is ignored — "(BO6)" names the game, not the PC — and so is
 * any digit glued to a letter, for the same reason ("BO6", "CS2"). Exactly one
 * standalone number must remain; none, or two ("Team 2 seat 7"), is null —
 * the row goes to the owner rather than to a guess.
 */
export function accountNumberOf(name: string): number | null {
  const outside = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ');
  const numbers = [...outside.matchAll(/(?<![\p{L}\p{N}])\p{Nd}+(?![\p{L}\p{N}])/gu)].map(
    (match) => match[0],
  );
  if (numbers.length !== 1) return null;
  const value = Number.parseInt(toAsciiDigits(numbers[0]!), 10);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * A device name's family and number: "PC-10" → PC 10, "VPC-3" → VPC 3,
 * "PC 7" → PC 7. The number must END the name; "MAIN-PC" has none and is
 * never proposed. The family is upper-cased so "pc-3" and "PC-3" are one.
 */
export function deviceNumberOf(name: string): { family: string; number: number } | null {
  const match = /^(.*?)[\s\-_#.]*(\p{Nd}+)$/u.exec(name.trim());
  if (!match) return null;
  const value = Number.parseInt(toAsciiDigits(match[2]!), 10);
  if (!Number.isSafeInteger(value)) return null;
  return { family: match[1]!.trim().toUpperCase(), number: value };
}

/** Arabic-Indic and Persian digits to ASCII, so "PC-٣" reads as PC 3. */
function toAsciiDigits(digits: string): string {
  return digits.replace(/[٠-٩۰-۹]/g, (digit) => String((digit.charCodeAt(0) & 0xf) % 10));
}

/* -------------------------------------------------------------------------- */
/* The proposal                                                               */
/* -------------------------------------------------------------------------- */

export type HomeDeviceDevice = Pick<DeviceDto, 'id' | 'name' | 'deviceType'>;
export type HomeDeviceAccount = Pick<
  GameAccountDto,
  'id' | 'accountName' | 'homeDeviceId' | 'homeDeviceName'
>;

/**
 * - `match`: exactly one PC carries the number — proposed.
 * - `unchanged`: the account already has its answer — that one PC, or, where
 *   the name alone cannot settle it, the PC the owner chose on an earlier pass
 *   (one of the several carrying the number, or any PC when none does).
 * - `ambiguous`: several PCs carry it (PC-3 and VPC-3) and the account is on
 *   none of them — the owner picks.
 * - `noMatch`: no number in the name, or no PC carries it, and no PC set yet.
 */
export const HOME_DEVICE_STATUSES = ['match', 'unchanged', 'ambiguous', 'noMatch'] as const;
export type HomeDeviceStatus = (typeof HOME_DEVICE_STATUSES)[number];

export interface HomeDeviceProposal {
  accountId: string;
  accountName: string;
  /** The PC the account is made for today; null = venue-wide. */
  currentDeviceId: string | null;
  currentDeviceName: string | null;
  number: number | null;
  status: HomeDeviceStatus;
  /** Every PC carrying the number — the choices an ambiguous row offers, and a settled one keeps. */
  candidates: HomeDeviceDevice[];
  /**
   * What the preview starts on: the match for `match`, and the account's
   * current PC for everything else — an ambiguous or unmatched row proposes
   * NO change until the owner picks one.
   */
  initialChoice: string | null;
}

/**
 * The proposal for every account, in natural name order ("escape 2" before
 * "escape 10"). Only PCs are candidates — a Table-10 or PS-10 is not the PC
 * an account was made for.
 *
 * When the account's own name names a family ("Gamers escape VPC-3"), only
 * that family is considered: that is the owner telling us, not us guessing.
 */
export function proposeHomeDevices(
  accounts: readonly HomeDeviceAccount[],
  devices: readonly HomeDeviceDevice[],
): HomeDeviceProposal[] {
  const pcs = devices
    .filter((device) => device.deviceType === 'PC')
    .flatMap((device) => {
      const parsed = deviceNumberOf(device.name);
      return parsed ? [{ device, ...parsed }] : [];
    });
  const families = new Set(pcs.map((pc) => pc.family).filter((family) => family !== ''));

  return [...accounts]
    .sort((a, b) => naturalCompare(a.accountName, b.accountName))
    .map((account) => {
      const current = account.homeDeviceId ?? null;
      const number = accountNumberOf(account.accountName);
      const named = familiesNamedIn(account.accountName, families);
      const candidates =
        number === null
          ? []
          : pcs
              .filter((pc) => pc.number === number)
              .filter((pc) => named.size === 0 || named.has(pc.family))
              .map((pc) => pc.device)
              .sort((a, b) => naturalCompare(a.name, b.name));

      // A row the owner has already answered is not a question any more: the
      // tool is reopened after every batch, and 25 of the venue's 61 accounts
      // can only ever be answered by hand — flagging them forever would bury
      // the rows still open under the ones already done.
      let status: HomeDeviceStatus;
      if (candidates.length === 1) status = candidates[0]!.id === current ? 'unchanged' : 'match';
      else if (candidates.length > 1)
        status = candidates.some((pc) => pc.id === current) ? 'unchanged' : 'ambiguous';
      else status = current !== null ? 'unchanged' : 'noMatch';

      return {
        accountId: account.id,
        accountName: account.accountName,
        currentDeviceId: current,
        currentDeviceName: account.homeDeviceName ?? null,
        number,
        status,
        candidates,
        initialChoice: status === 'match' ? candidates[0]!.id : current,
      };
    });
}

/** The device families spelled out as words in an account's name. */
function familiesNamedIn(accountName: string, families: ReadonlySet<string>): Set<string> {
  const words = accountName.toUpperCase().split(/[^\p{L}]+/u);
  return new Set(words.filter((word) => families.has(word)));
}

export type HomeDeviceSummary = Record<HomeDeviceStatus, number>;

/** How many rows are in each state — the line above the preview table. */
export function summariseProposals(rows: readonly HomeDeviceProposal[]): HomeDeviceSummary {
  const summary: HomeDeviceSummary = { match: 0, unchanged: 0, ambiguous: 0, noMatch: 0 };
  for (const row of rows) summary[row.status] += 1;
  return summary;
}

/** The owner's choices, keyed by account id; null = venue-wide. */
export type HomeDeviceChoices = Record<string, string | null>;

export function initialChoices(rows: readonly HomeDeviceProposal[]): HomeDeviceChoices {
  return Object.fromEntries(rows.map((row) => [row.accountId, row.initialChoice]));
}

/**
 * The body "Apply" posts: every row whose final choice differs from what the
 * account already has, and nothing else — so `updated` in the answer is the
 * number the button promised. An empty list means there is nothing to send.
 */
export function assignmentsToApply(
  rows: readonly HomeDeviceProposal[],
  choices: HomeDeviceChoices,
): BulkAssignHomeDevicesInput['assignments'] {
  return rows.flatMap((row) => {
    const choice = row.accountId in choices ? (choices[row.accountId] ?? null) : row.initialChoice;
    return choice === row.currentDeviceId ? [] : [{ accountId: row.accountId, deviceId: choice }];
  });
}

/* -------------------------------------------------------------------------- */
/* Device pickers                                                             */
/* -------------------------------------------------------------------------- */

export interface DeviceOptionGroup {
  /** The family ("PC", "VPC") for PCs; null for the "other devices" group. */
  family: string | null;
  devices: HomeDeviceDevice[];
}

/**
 * The "Made for PC" choices, PCs first: one group per PC family, so VPC-3
 * sits in a group of its own rather than between PC-29 and PC-3; then every
 * other device in one last group. Natural order inside each.
 */
export function deviceOptionGroups(devices: readonly HomeDeviceDevice[]): DeviceOptionGroup[] {
  const byFamily = new Map<string, HomeDeviceDevice[]>();
  const others: HomeDeviceDevice[] = [];
  for (const device of devices) {
    if (device.deviceType !== 'PC') {
      others.push(device);
      continue;
    }
    const family = deviceNumberOf(device.name)?.family || 'PC';
    const list = byFamily.get(family) ?? [];
    list.push(device);
    byFamily.set(family, list);
  }
  const byName = (a: HomeDeviceDevice, b: HomeDeviceDevice) => naturalCompare(a.name, b.name);
  // Numbered machines first, then the odd one out (MAIN-PC) at the end of its
  // group rather than ahead of PC-1.
  const numberedFirst = (a: HomeDeviceDevice, b: HomeDeviceDevice) =>
    Number(deviceNumberOf(a.name) === null) - Number(deviceNumberOf(b.name) === null) ||
    byName(a, b);
  const groups: DeviceOptionGroup[] = [...byFamily.entries()]
    // "PC" before "VPC": the plain PCs are the floor; the rest are the extras.
    .sort(([a], [b]) => (a === 'PC' ? -1 : b === 'PC' ? 1 : naturalCompare(a, b)))
    .map(([family, list]) => ({ family, devices: list.sort(numberedFirst) }));
  if (others.length > 0) groups.push({ family: null, devices: others.sort(byName) });
  return groups;
}

/* -------------------------------------------------------------------------- */
/* The list: PC filter and selection helpers                                  */
/* -------------------------------------------------------------------------- */

/** The PC filter's "venue-wide accounts only" value; '' is "every PC". */
export const NO_HOME_DEVICE = '__none__';

/** The PCs the listed accounts are made for, for the filter — from the list itself. */
export function homeDeviceFilterOptions(
  accounts: readonly Pick<GameAccountDto, 'homeDeviceId' | 'homeDeviceName'>[],
): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const account of accounts) {
    if (account.homeDeviceId) {
      seen.set(account.homeDeviceId, account.homeDeviceName ?? account.homeDeviceId);
    }
  }
  return [...seen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => naturalCompare(a.name, b.name));
}

export function filterByHomeDevice<T extends Pick<GameAccountDto, 'homeDeviceId'>>(
  accounts: readonly T[],
  filter: string,
): T[] {
  if (filter === '') return [...accounts];
  if (filter === NO_HOME_DEVICE) return accounts.filter((account) => !account.homeDeviceId);
  return accounts.filter((account) => account.homeDeviceId === filter);
}

/**
 * "Select by name contains": the ids of the listed accounts whose name holds
 * the text, case-insensitively — "(BO6)" ticks the thirty BO6 accounts.
 * Blank selects nothing: a stray click must not tick the whole venue.
 */
export function idsWhoseNameContains(
  accounts: readonly Pick<GameAccountDto, 'id' | 'accountName'>[],
  text: string,
): string[] {
  const needle = text.trim().toLocaleLowerCase();
  if (!needle) return [];
  return accounts
    .filter((account) => account.accountName.toLocaleLowerCase().includes(needle))
    .map((account) => account.id);
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** "escape 2" before "escape 10", "PC-9" before "PC-10". */
export function naturalCompare(a: string, b: string): number {
  return collator.compare(a, b);
}
